import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUserCircuitRankings } from '@/hooks/useUserCircuitRankings';
import { useCircuitLeaderboard } from '@/hooks/useCircuitLeaderboard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Trophy, TrendingUp, ChevronRight, ChevronLeft, ChevronDown, Loader2, Calendar } from 'lucide-react';

interface ProfileRankingViewProps {
  userId?: string;
}

const ProfileRankingView: React.FC<ProfileRankingViewProps> = ({ userId }) => {
  const { t } = useLanguage();
  const { rankings, loading } = useUserCircuitRankings(userId);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedRanking = rankings.find(r => r.id === selectedId);
  const selectedCircuitId = selectedRanking ? selectedRanking.id.split('::')[0] : null;

  const { leaderboard, tournaments, loading: leaderboardLoading } = useCircuitLeaderboard(
    selectedCircuitId,
    selectedRanking?.category || '',
    selectedRanking?.subcategory || ''
  );

  if (loading) {
    return (
      <div className="space-y-3 px-1">
        {[1, 2].map(i => (
          <div key={i} className="h-20 bg-muted/50 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (!rankings.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
          <Trophy className="h-8 w-8 text-muted-foreground/50" />
        </div>
        <p className="text-sm font-medium text-foreground mb-1">{t('profile.noRankings')}</p>
        <p className="text-xs text-muted-foreground">{t('profile.noRankingsDesc')}</p>
      </div>
    );
  }

  // Detail view
  const currentCircuitId = selectedRanking ? selectedRanking.id.split('::')[0] : null;
  const circuitRankings = currentCircuitId
    ? rankings.filter(r => r.id.startsWith(`${currentCircuitId}::`))
    : [];

  if (selectedRanking) {
    const formatDate = (dateStr: string) => {
      try {
        return new Date(dateStr).toLocaleDateString('es-MX', {
          day: 'numeric', month: 'short', year: 'numeric'
        });
      } catch { return dateStr; }
    };

    return (
      <div className="space-y-4">
        <button
          onClick={() => setSelectedId(null)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Volver
        </button>

        <div className="flex flex-col items-center text-center space-y-2 pb-2">
          <Avatar className="h-16 w-16">
            <AvatarImage src={selectedRanking.organizer_avatar || undefined} />
            <AvatarFallback className="text-lg bg-primary/10 text-primary font-semibold">
              {selectedRanking.organizer_name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-0.5">
            <p className="text-base font-semibold text-foreground">{selectedRanking.organizer_name}</p>
            {selectedRanking.circuit_name && (
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                {selectedRanking.circuit_name}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {selectedRanking.sport}
            </p>
          </div>
        </div>

        {circuitRankings.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1 px-1">
            {circuitRankings.map(r => (
              <button
                key={r.id}
                onClick={() => setSelectedId(r.id)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  selectedId === r.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`}
              >
                {r.category}{r.subcategory ? ` · ${r.subcategory}` : ''}
              </button>
            ))}
          </div>
        )}

        {leaderboardLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Tournaments section - collapsible */}
            {tournaments.length > 0 && (
              <Collapsible>
                <CollapsibleTrigger className="flex items-center justify-between w-full px-1 py-1 group">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Torneos del circuito ({tournaments.length})
                  </p>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-1.5 mt-2">
                    {tournaments.map((t, idx) => (
                      <div key={t.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/30 border">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-bold shrink-0">
                          T{idx + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground truncate">{t.name}</p>
                          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(t.date)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Ranking table */}
            {leaderboard.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">Sin participantes</p>
            ) : (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-1">
                  Ranking — {selectedRanking.category}{selectedRanking.subcategory ? ` · ${selectedRanking.subcategory}` : ''}
                </p>
                <div className="rounded-lg border overflow-hidden bg-card">
                  <div className="overflow-x-auto">
                    <div className="min-w-fit">
                      {/* Header */}
                      <div className="flex text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 bg-muted/30 border-b">
                        <span className="w-8 shrink-0">#</span>
                        <span className="flex-1 min-w-[100px]">Jugador</span>
                        {tournaments.map((t, idx) => (
                          <span key={t.id} className="w-12 text-center shrink-0">T{idx + 1}</span>
                        ))}
                        <span className="w-14 text-right shrink-0">Total</span>
                      </div>
                      {/* Rows */}
                      <div className="divide-y divide-border">
                        {leaderboard.map((entry, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center px-3 py-2 text-sm ${
                              entry.isCurrentUser ? 'bg-primary/10 font-semibold' : 'bg-card'
                            }`}
                          >
                            <span className="w-8 shrink-0 text-muted-foreground font-medium">{entry.position}</span>
                            <span className="flex-1 min-w-[100px] truncate text-foreground">
                              {entry.name}
                              {entry.isCurrentUser && <span className="text-primary ml-1 text-xs">(tú)</span>}
                            </span>
                            {tournaments.map(t => (
                              <span key={t.id} className="w-12 text-center shrink-0 text-xs text-muted-foreground">
                                {entry.pointsPerTournament[t.id] ?? '-'}
                              </span>
                            ))}
                            <span className="w-14 text-right shrink-0 font-medium text-foreground">{entry.points}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="bg-card border rounded-xl overflow-hidden divide-y divide-border">
      {rankings.map(ranking => (
        <button
          key={ranking.id}
          onClick={() => setSelectedId(ranking.id)}
          className="w-full px-4 py-3.5 flex items-center gap-3 hover:bg-muted/30 transition-colors text-left"
        >
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarImage src={ranking.organizer_avatar || undefined} />
            <AvatarFallback className="text-xs bg-primary/10 text-primary font-semibold">
              {ranking.organizer_name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0 space-y-0.5">
            <p className="text-sm font-semibold text-foreground truncate">
              {ranking.organizer_name}
            </p>
            {ranking.circuit_name && (
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide truncate">
                {ranking.circuit_name}
              </p>
            )}
            <p className="text-xs text-muted-foreground truncate">
              {ranking.sport}
              {ranking.category ? ` · ${ranking.category}` : ''}
              {ranking.subcategory ? ` · ${ranking.subcategory}` : ''}
            </p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                {ranking.total_points} pts
              </span>
              <span>{ranking.tournaments_played} {ranking.tournaments_played === 1 ? 'torneo' : 'torneos'}</span>
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-1.5">
            <span className="text-lg font-bold text-primary">#{ranking.position}</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </button>
      ))}
    </div>
  );
};

export default ProfileRankingView;
